import * as React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LoadingRows } from './LoadingState';
import { EmptyState } from './EmptyState';
import { cn } from '@/lib/utils';

export type TableDensity = 'compact' | 'comfortable';

export interface Column<T> {
  key: string;
  header: React.ReactNode;
  cell: (row: T) => React.ReactNode;
  className?: string;
  headerClassName?: string;
  align?: 'left' | 'right' | 'center';
  width?: string;
  /** When true, the column's cell content fades in only on row hover.
   *  Use for inline action clusters (Open · Edit · ⋯). */
  hoverReveal?: boolean;
}

/**
 * Pagination configuration. Two modes:
 *
 * 1. **Client-side (default)** — pass just `paginated`. DataTable slices
 *    `rows` itself and tracks page state internally. `defaultPageSize`
 *    seeds the picker (default 25).
 *
 * 2. **Controlled / server-side** — pass `page`, `pageSize`, `total`,
 *    `onPageChange` and `onPageSizeChange`. DataTable renders `rows`
 *    as-is and the parent fetches by page.
 */
export interface PaginationProps {
  /** Set to `false` to render with no footer. Defaults to `true`. */
  paginated?: boolean;
  /** Controlled page (1-indexed). When provided, switches to server mode. */
  page?: number;
  pageSize?: number;
  /** Server-side total. Required for server mode; ignored in client mode. */
  total?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
}

interface Props<T> extends PaginationProps {
  columns: Column<T>[];
  rows: T[] | undefined;
  rowKey: (row: T) => string | number;
  isLoading?: boolean;
  onRowClick?: (row: T) => void;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: React.ReactNode;
  caption?: React.ReactNode;
  className?: string;
  /** Visual row spacing. `compact` mirrors Linear; `comfortable` is the legacy default. */
  density?: TableDensity;
  /** When true, the header sticks to the top of the scroll container. */
  stickyHeader?: boolean;
}

const DENSITY_CELL: Record<TableDensity, string> = {
  compact: 'py-1.5 text-[13px]',
  comfortable: 'py-3 text-sm',
};

const DENSITY_HEAD: Record<TableDensity, string> = {
  compact: 'h-8',
  comfortable: 'h-10',
};

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  isLoading,
  onRowClick,
  emptyTitle = 'No records yet',
  emptyDescription,
  emptyAction,
  className,
  density = 'comfortable',
  stickyHeader = false,
  paginated = true,
  page: controlledPage,
  pageSize: controlledPageSize,
  total: controlledTotal,
  onPageChange,
  onPageSizeChange,
  defaultPageSize = 25,
  pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
}: Props<T>) {
  // ──────────────────────────────────────────────────────────────────
  // Pagination state. When `controlledPage`/`controlledPageSize` are
  // passed, we treat the table as server-driven (parent fetches per
  // page); otherwise we slice client-side and track our own state.
  // ──────────────────────────────────────────────────────────────────
  const isControlled = controlledPage !== undefined;
  const [internalPage, setInternalPage] = React.useState(1);
  const [internalPageSize, setInternalPageSize] = React.useState(defaultPageSize);
  const page = isControlled ? controlledPage : internalPage;
  const pageSize = controlledPageSize ?? internalPageSize;
  const total = isControlled
    ? (controlledTotal ?? (rows?.length ?? 0))
    : (rows?.length ?? 0);
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  // Clamp internal page if the row set shrinks beneath the current
  // window (e.g. after filtering). Skip for controlled tables — the
  // parent owns the page state.
  React.useEffect(() => {
    if (isControlled) return;
    if (internalPage > pageCount) {
      setInternalPage(pageCount);
    }
  }, [pageCount, internalPage, isControlled]);

  const setPage = (next: number) => {
    const clamped = Math.min(Math.max(1, next), pageCount);
    if (isControlled) onPageChange?.(clamped);
    else setInternalPage(clamped);
  };
  const setPageSize = (next: number) => {
    if (isControlled) onPageSizeChange?.(next);
    else {
      setInternalPageSize(next);
      setInternalPage(1);
    }
  };

  const visibleRows = React.useMemo(() => {
    if (!rows) return rows;
    if (!paginated || isControlled) return rows;
    const start = (page - 1) * pageSize;
    return rows.slice(start, start + pageSize);
  }, [rows, paginated, isControlled, page, pageSize]);

  // ── Loading / empty branches ─────────────────────────────────────
  if (isLoading) {
    return (
      <div className={cn('overflow-hidden rounded-xl border border-border/70 bg-card shadow-elev-1', className)}>
        <div className="px-4 pt-4">
          <LoadingRows rows={6} cols={Math.max(columns.length, 4)} />
        </div>
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
    );
  }

  // ── Render ───────────────────────────────────────────────────────
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(page * pageSize, total);
  const showFooter = paginated;

  return (
    <div className={cn('overflow-hidden rounded-xl border border-border/70 bg-card shadow-elev-1', className)}>
      <Table>
        <TableHeader
          className={cn(
            stickyHeader && 'sticky top-0 z-10 backdrop-blur supports-[backdrop-filter]:bg-card/80',
          )}
        >
          <TableRow>
            {columns.map((col) => (
              <TableHead
                key={col.key}
                className={cn(
                  DENSITY_HEAD[density],
                  col.align === 'right' && 'text-right',
                  col.align === 'center' && 'text-center',
                  col.headerClassName,
                )}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.header}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {(visibleRows ?? []).map((row) => (
            <TableRow
              key={rowKey(row)}
              className={cn('group/row', onRowClick && 'cursor-pointer')}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((col) => (
                <TableCell
                  key={col.key}
                  className={cn(
                    DENSITY_CELL[density],
                    col.align === 'right' && 'text-right',
                    col.align === 'center' && 'text-center',
                    col.hoverReveal &&
                      'opacity-0 transition-opacity group-hover/row:opacity-100 focus-within:opacity-100',
                    col.className,
                  )}
                  onClick={
                    // Stop row-click propagation so action buttons inside a
                    // hoverReveal column don't also open the detail view.
                    col.hoverReveal ? (e) => e.stopPropagation() : undefined
                  }
                >
                  {col.cell(row)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {showFooter && (
        <DataTableFooter
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          total={total}
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      )}
    </div>
  );
}

interface FooterProps {
  rangeStart: number;
  rangeEnd: number;
  total: number;
  page: number;
  pageCount: number;
  pageSize: number;
  pageSizeOptions: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

function DataTableFooter({
  rangeStart,
  rangeEnd,
  total,
  page,
  pageCount,
  pageSize,
  pageSizeOptions,
  onPageChange,
  onPageSizeChange,
}: FooterProps) {
  const atFirst = page <= 1;
  const atLast = page >= pageCount;
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border/70 bg-muted/20 px-4 py-2 text-[12px] text-muted-foreground">
      <div className="flex items-center gap-2">
        <span className="hidden sm:inline">Rows per page</span>
        <Select
          value={String(pageSize)}
          onValueChange={(v) => onPageSizeChange(Number(v))}
        >
          <SelectTrigger className="h-7 w-[72px] text-[12px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {pageSizeOptions.map((opt) => (
              <SelectItem key={opt} value={String(opt)} className="text-[12px]">
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span aria-live="polite" className="tabular-nums">
          {total === 0
            ? 'No records'
            : `Showing ${rangeStart.toLocaleString()}–${rangeEnd.toLocaleString()} of ${total.toLocaleString()}`}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <span className="hidden tabular-nums sm:mr-2 sm:inline">
          Page {page} of {pageCount}
        </span>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => onPageChange(1)}
          disabled={atFirst}
          aria-label="First page"
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => onPageChange(page - 1)}
          disabled={atFirst}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => onPageChange(page + 1)}
          disabled={atLast}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0"
          onClick={() => onPageChange(pageCount)}
          disabled={atLast}
          aria-label="Last page"
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
